// Registry of Drip vaults. One descriptor per deposit asset.
//
// The Drip program exposes two instruction families: the original kySOL-only
// instructions (unseeded PDAs) and a generic asset-vault family whose PDAs are
// seeded by the deposit mint. `kind` selects which family a vault uses; it is
// not a cosmetic label. See src/features/drip/dripPdas.ts.

export type DripVaultKind = "kysol" | "asset";
export type DripVaultId = "kysol" | "hsol";
export type DripInputAsset = "SOL" | "JitoSOL" | "kySOL" | "hSOL";

export interface DripVaultDescriptor {
  id: DripVaultId;
  kind: DripVaultKind;
  /** Display symbol of the deposited asset. */
  symbol: string;
  depositMint: string;
  /** Bundled icon path. Omit to resolve remotely from the mint (useDripVaultIcon). */
  icon?: string;
  /** Assets the deposit tab accepts for this vault, in display order. */
  inputAssets: readonly DripInputAsset[];
  /** Minimum resulting position, in units of the deposit asset. */
  minDeposit: number;
}

// Immutable public mainnet address, pinned like YIELD_TOKEN_CONTRACTS.
//
// No stake pool is pinned here even though deposit_asset and redeem_asset now DO
// take one as an account: the program constrains it against
// `AssetVault.stake_pool`, so the only safe source is that account itself. See
// fetchDripOracleAccounts, which reads it per call. A pinned copy here could drift
// and would fail with InvalidAccountData (6011).
export const HSOL_MINT = "he1iusmfkpAdwvxLNGV8Y1iSbj4rUy6yMhEA3fotn9A";

export interface DripVaultsConfig {
  /** kySOL mint; environment-dependent, hence the builder rather than a const. */
  solVrtMintAddress: string;
  dripHsolEnabled: boolean;
}

/**
 * Returns the enabled vaults in display order. Consumers iterate this rather
 * than branching on vault identity, so adding an LST is a new entry here.
 *
 * Deposit mints must have 9 decimals; the program rejects anything else with
 * UnsupportedMintDecimals (6015).
 */
export const buildDripVaults = (config: DripVaultsConfig): DripVaultDescriptor[] => {
  const vaults: DripVaultDescriptor[] = [
    {
      id: "kysol",
      kind: "kysol",
      symbol: "kySOL",
      depositMint: config.solVrtMintAddress,
      icon: "/kysol.png",
      inputAssets: ["SOL", "JitoSOL", "kySOL"],
      minDeposit: 1,
    },
  ];

  if (config.dripHsolEnabled) {
    vaults.push({
      id: "hsol",
      kind: "asset",
      symbol: "hSOL",
      depositMint: HSOL_MINT,
      icon: "/hsol.png",
      inputAssets: ["hSOL"],
      minDeposit: 1,
    });
  }

  return vaults;
};

export const findVaultForInputAsset = (
  vaults: DripVaultDescriptor[],
  asset: string
): DripVaultDescriptor | undefined =>
  vaults.find((vault) => vault.inputAssets.some((candidate) => candidate === asset));

export const findVaultById = (
  vaults: DripVaultDescriptor[],
  id: string
): DripVaultDescriptor | undefined => vaults.find((vault) => vault.id === id);
